/**
 * Shared helpers for turning a failed HTTP response into a user-facing message. The backend returns a
 * {@link ServerError}-shaped body ({@code errorMessage}), and the engine sometimes fronts a downstream (e.g. oltp)
 * error by appending its JSON body to its own text — these helpers normalise both into a single display string.
 */

/**
 * Unwraps an engine-wrapped error string of the form {@code "...prefix...: {"errorMessage":"..."}"} to its inner
 * {@code errorMessage}. The engine fronts downstream errors by appending their JSON body to its own text, so this
 * extracts the meaningful inner message. Returns the input unchanged when it is not such an envelope.
 *
 * @param raw the raw error text, possibly containing a trailing JSON envelope
 * @returns the inner {@code errorMessage} if present, otherwise the original string
 */
export function unwrapServerErrorMessage(raw: string): string
{
    const braceIndex = raw.indexOf('{');
    if (braceIndex >= 0)
    {
        try
        {
            const parsed = JSON.parse(raw.slice(braceIndex));
            if (parsed?.errorMessage)
            {
                return parsed.errorMessage;
            }
        }
        catch
        {
            // Not a JSON envelope — fall back to the raw text.
        }
    }
    return raw;
}

/**
 * Derives a user-facing message from a failed API response, preferring a plain-text {@code error} body (some
 * endpoints return text rather than JSON), then the backend's {@code errorMessage}, then a generic {@code message},
 * then the thrown error's own message, and finally the supplied fallback. The chosen string is passed through
 * {@link unwrapServerErrorMessage} so an engine-wrapped JSON envelope surfaces its inner message.
 *
 * @param err the error caught from an API call (HttpErrorResponse-shaped, but typed loosely)
 * @param fallback message to show when no usable text can be extracted
 * @returns the resolved message, never empty
 */
export function extractServerErrorMessage(err: any, fallback: string): string
{
    const raw = typeof err?.error === 'string'
        ? err.error
        : err?.error?.errorMessage ?? err?.error?.message ?? err?.message ?? '';
    return unwrapServerErrorMessage(raw) || fallback;
}
